import { useState } from "react";
import { Workflow, WorkflowAutomationItem } from "./interface";
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from "@heroicons/react/outline";
import { MIMETYPE_ICON_MAP } from "./utils";
import { postData } from "./backend";
import Breadcrumbs from "./Breadcrumbs";
import { useTranslation } from "react-i18next";
import PDFViewer from "./PDFViewer";

interface Props {
  token: string;
  workflow?: Workflow;
  automationItem?: WorkflowAutomationItem;
}

function AppWorkflowPreview(props: Props) {
  const { t } = useTranslation();

  const { token, workflow } = props;

  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);

  const [approveState, setApproveState] = useState<"PENDING" | "APPROVING" | "APPROVED" | "REJECTED">("PENDING");

  const outputs = props.automationItem?.outputs || [];

  const file = outputs?.[currentDocumentIndex]?.file || outputs?.[currentDocumentIndex]?.filePdf;
  const microsoftFile = outputs?.[currentDocumentIndex]?.onedriveFile;

  const hubspotFile = outputs?.[currentDocumentIndex]?.hubspotFilePdf;
  const previewUrl = outputs?.[currentDocumentIndex]?.previewUrl; // NOTE: assumes hubspotFile is valid

  const handleChangeDocument = (direction: -1 | 1) => {
    const newIndex = Math.min(Math.max(0, currentDocumentIndex + direction), outputs.length - 1);

    if (newIndex !== currentDocumentIndex && outputs[newIndex]) {
      setCurrentDocumentIndex(newIndex);
    }
  }

  function approveAutomationItem() {
    setApproveState("APPROVING");
    postData(`/hubspot/app/automation-items/${props.automationItem?.id}/`, token)
      .then(() => setApproveState("APPROVED"))
      .catch((error) => { console.error(error); setApproveState("PENDING"); });
  }

  if (!props.automationItem)
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className='w-32 h-32' />
        <div className="text-gray-400 font-semibold text-center text-sm mt-2">{t("Loading")}...</div>
      </div>
    );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Breadcrumbs workflow={workflow} currentPage="PREVIEW" />

      <div className="flex h-full">
        <div className="flex w-full gap-8 justify-center">

          <div className="flex flex-col items-start h-full w-[1000px] flex-shrink-0 bg-[#f9fbfd] overflow-hidden">
            <div className="bg-white w-full flex justify-between pb-2">
              <div className="flex items-center gap-1 font-bold text-sm">
                {(previewUrl) &&
                  <a className="flex items-center gap-2 text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" href={hubspotFile?.fileUrl} target="_blank" rel="noreferrer">
                    <img className="h-5 w-5 mr-2 rounded-md" src={MIMETYPE_ICON_MAP["application/pdf"]} alt="PDF File Icon" />
                    <h3 className="truncate max-w-[400px]"> {hubspotFile?.fileName}</h3>
                    {hubspotFile?.fileUrl && <ExternalLinkIcon className="h-4 w-4 group-hover:text-blue" />}
                  </a>
                }
                {(!previewUrl && microsoftFile) &&
                  <a className="flex items-center gap-2 text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" href={microsoftFile.fileUrl} target="_blank" rel="noreferrer">
                    <img className="h-5 w-5 mr-2 rounded-md" src={MIMETYPE_ICON_MAP[microsoftFile?.fileMimeType!]} alt="Microsoft File Icon" />
                    <h3 className="truncate max-w-[400px]"> {microsoftFile?.fileName}</h3>
                    {microsoftFile?.fileUrl && <ExternalLinkIcon className="h-4 w-4 group-hover:text-blue" />}
                  </a>
                }
                {(!previewUrl && !microsoftFile && file) &&
                  <a className="flex items-center gap-2 text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" href={file?.url} target="_blank" rel="noreferrer">
                    <img className="h-5 w-5 mr-2 rounded-md" src={MIMETYPE_ICON_MAP[file?.mimeType!]} alt="Google File Icon" />
                    <h3 className="truncate max-w-[400px]"> {file?.name}</h3>
                    {file?.url && <ExternalLinkIcon className="h-4 w-4 group-hover:text-blue" />}
                  </a>
                }
              </div>
              <div className="flex items-center gap-4">
                {outputs.length > 1 &&
                  <div className="flex items-center ml-auto">
                    <ChevronLeftIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={() => handleChangeDocument(-1)} />
                    <div className="font-semibold text-gray-600 font-gilroy w-12 text-center">
                      {currentDocumentIndex + 1} / {outputs.length}
                    </div>
                    <ChevronRightIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={() => handleChangeDocument(1)} />
                  </div>
                }
                {(props.automationItem?.stage === "REVIEW_PENDING" && props.automationItem.approvableByUser) &&
                  <>
                    {approveState === "PENDING" &&
                      <button className="btn btn-green text-sm h-8 w-28" onClick={approveAutomationItem}>
                        {t("Approve")}
                      </button>
                    }
                    {approveState === "APPROVING" &&
                      <button className="btn btn-green bg-white text-green text-sm h-8 w-28" disabled>
                        {t("Approving")}...
                      </button>
                    }
                    {approveState === "APPROVED" &&
                      <button className="btn btn-green bg-white text-green text-sm h-8 w-28">
                        {t("Approved")}
                      </button>
                    }
                  </>
                }
              </div>
            </div>
            {previewUrl &&
              <div className="flex flex-col w-full rounded flex-shrink-0" style={{ height: "calc(100% - 32px)" }}>
                <PDFViewer pdfUrl={previewUrl} />
              </div>
            }
            {(!previewUrl && microsoftFile?.fileEmbedViewUrl) &&
              <iframe title={t("Microsoft Editor")} className="w-full h-full rounded overflow-hidden" src={microsoftFile.fileEmbedViewUrl} />
            }
            {(!previewUrl && !microsoftFile?.fileEmbedViewUrl && file?.url) &&
              <iframe title={t("Google Editor")} className="w-full h-full border border-gray-300 rounded overflow-hidden" src={`${file?.url?.replace("/edit", props.automationItem.approvableByUser ? "/edit" : "/preview").replace("/view", "/preview")}?widget&rm=minimal`} />
            }
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppWorkflowPreview;
